/**
 * src/hooks/use-presets.js
 *
 * Top-level preset and user-tuning state manager.
 *
 * This hook mediates between built-in tunings, user tunings, session restore,
 * dirty-state tracking, and preset switching. App uses it as the owner of
 * "which tuning is active" and "what should happen when that source changes".
 */
import { useState, useEffect, useMemo } from "preact/hooks";
import { IO_SETTING_KEYS } from "../persistence/io-reload-policy.js";
import {
  defaultTuningRecord,
  findPresetTuningByName,
  presetTuningGroups,
} from "../hexatone/preset-tunings/index.js";
import { normalizeTuningRecord, settingsToTuningRecord } from "../hexatone/tuning-record.js";
import { settingsToAbletonScala } from "../settings/scale/parse-scale.js";
import { loadUserTunings } from "../hexatone/user-tunings.js";
import { PRESET_SKIP_KEYS, buildRegistryDefaults } from "../persistence/settings-registry.js";
import { normalizeModulationHistory } from "../tuning/modulation-runtime.js";
import { getControllerById, getControllerMpeInputPolicy } from "../controllers/registry.js";
import { CONTROLLER_PRESET_ANCHOR_CONFIGS } from "../controllers/preset-anchors.js";
import { loadSavedAnchor, loadSavedAnchorChannel } from "../input/controller-anchor.js";
import { deriveKeyColorFlags } from "../settings/scale/key-colors-mode.js";
import { primeSharedSampleAudio } from "../sample_synth/prime-shared-audio.js";

export { PRESET_SKIP_KEYS };
const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Scale-related keys to clear on reset (keeps output settings)
export const SCALE_KEYS_TO_CLEAR = [
  "scale",
  "scale_import",
  "note_names",
  "note_colors",
  "key_labels",
  "fundamental",
  "reference_degree",
  "equivSteps",
  "equivInterval",
  "rSteps",
  "drSteps",
  "hexSize",
  "rotation",
  "center_degree",
  // "midiin_anchor_note" excluded — hardware setting, persists across presets
  "key_colors_mode",
  "auto_colors",
  "spectrum_colors",
  "fundamental_color",
  "name",
  "description",
  "short_description",
];

export const clearScaleSettings = () => {
  SCALE_KEYS_TO_CLEAR.forEach((key) => sessionStorage.removeItem(key));
};

function buildBlankPresetSettings() {
  return {
    ...buildRegistryDefaults(),
    name: "",
    description: "",
    short_description: "",
    scale_import: null,
    scale: null,
    note_names: null,
    note_colors: null,
  };
}

export const findPreset = (preset) => {
  const matchedPreset = findPresetTuningByName(preset) ?? defaultTuningRecord;
  return {
    ...matchedPreset,
    scale_import: settingsToAbletonScala(matchedPreset),
  };
};

function isBuiltinPresetName(name) {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) return false;
  for (const group of presetTuningGroups) {
    for (const preset of group.settings) {
      if (preset.name === trimmed) return true;
    }
  }
  return false;
}

function hasRestorableWorkspace(settings = {}) {
  return Array.isArray(settings?.scale) && settings.scale.length > 0;
}

// Scale preset hexSize down on phone-sized screens, but not below 20.
// Use the display's short edge so phone landscape matches portrait behaviour.
// Window dimensions also describe split desktop windows, not just phones.
export const scaleHexSizeForScreen = (hexSize) => {
  const size = hexSize || 42;
  const shortEdge =
    typeof window === "undefined"
      ? Infinity
      : Math.min(window.screen?.width || Infinity, window.screen?.height || Infinity);
  if (shortEdge <= 600 && size > 31) {
    return Math.max(20, Math.floor(size * 0.75));
  }
  return size;
};

function sessionIntOrFallback(key, fallback) {
  if (typeof sessionStorage === "undefined") return fallback;
  const raw = sessionStorage.getItem(key);
  if (raw === null) return fallback;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function sessionIntFromAliases(keys, fallback) {
  for (const key of keys) {
    const value = sessionIntOrFallback(key, null);
    if (Number.isFinite(value)) return value;
  }
  return fallback;
}

function restorePersistentAnchorFields(fallback = {}) {
  return {
    midiin_anchor_note: sessionIntFromAliases(
      ["midiin_anchor_note", "midiin_central_degree", "lumatone_center_note"],
      fallback.midiin_anchor_note ?? fallback.midiin_central_degree ?? 60,
    ),
    midiin_anchor_channel: sessionIntFromAliases(
      ["midiin_anchor_channel", "lumatone_center_channel"],
      fallback.midiin_anchor_channel ?? 1,
    ),
  };
}

const PRESET_ANCHOR_CONFIGS = CONTROLLER_PRESET_ANCHOR_CONFIGS.map((config) => ({
  ...config,
  controller: getControllerById(config.controllerId),
}));

function hasPresetAnchor(settings = {}) {
  return PRESET_ANCHOR_CONFIGS.some(
    ({ noteKey, channelKey }) =>
      Number.isFinite(settings[noteKey]) || (channelKey && Number.isFinite(settings[channelKey])),
  );
}

function getPresetAnchorConfig(settings = {}, preferredControllerId = null) {
  const hasAnchor = ({ noteKey, channelKey }) =>
    Number.isFinite(settings[noteKey]) || (channelKey && Number.isFinite(settings[channelKey]));
  const preferred = PRESET_ANCHOR_CONFIGS.find(
    (config) => config.controllerId === preferredControllerId && hasAnchor(config),
  );
  return (
    preferred ??
    PRESET_ANCHOR_CONFIGS.find(
      ({ noteKey, channelKey }) =>
        Number.isFinite(settings[noteKey]) || (channelKey && Number.isFinite(settings[channelKey])),
    )
  );
}

function getAnchorFallback(settings = {}) {
  if (!hasPresetAnchor(settings)) {
    return {
      midiin_anchor_note: settings.midiin_anchor_note ?? 60,
      midiin_anchor_channel: settings.midiin_anchor_channel ?? 1,
    };
  }

  const presetAnchorConfig = getPresetAnchorConfig(settings, settings.midiin_controller_override);
  if (!presetAnchorConfig?.controller) {
    return {
      midiin_anchor_note: settings.midiin_anchor_note ?? 60,
      midiin_anchor_channel: settings.midiin_anchor_channel ?? 1,
    };
  }

  return {
    midiin_anchor_note: loadSavedAnchor(presetAnchorConfig.controller, settings, {
      preferStored: false,
    }),
    midiin_anchor_channel:
      loadSavedAnchorChannel(presetAnchorConfig.controller, settings, {
        preferStored: false,
      }) ?? 1,
  };
}

// HEJI anchors may be auto-derived from the current preset's tuning/labels.
// Reset them on preset load so an inferred anchor from the previous preset
// cannot survive the merge unless the incoming preset explicitly defines one.
export const mergePresetIntoSettings = (settings, preset) => {
  const persistentAnchorFallback = getAnchorFallback(settings);
  const restoredAnchor = restorePersistentAnchorFields(persistentAnchorFallback);
  const activePresetAnchorConfig = getPresetAnchorConfig(
    preset,
    settings.midiin_controller_override,
  );
  const presetAnchorNote =
    activePresetAnchorConfig && Number.isFinite(preset[activePresetAnchorConfig.noteKey])
      ? preset[activePresetAnchorConfig.noteKey]
      : restoredAnchor.midiin_anchor_note;
  const presetAnchorChannel =
    activePresetAnchorConfig?.channelKey &&
    Number.isFinite(preset[activePresetAnchorConfig.channelKey])
      ? preset[activePresetAnchorConfig.channelKey]
      : restoredAnchor.midiin_anchor_channel;

  const clearedPresetAnchorFields = Object.fromEntries(
    PRESET_ANCHOR_CONFIGS.flatMap(({ noteKey, channelKey }) => [
      [noteKey, undefined],
      ...(channelKey ? [[channelKey, undefined]] : []),
    ]),
  );
  const presetWithoutControllerAnchors = { ...preset };
  for (const { noteKey, channelKey } of PRESET_ANCHOR_CONFIGS) {
    delete presetWithoutControllerAnchors[noteKey];
    if (channelKey) delete presetWithoutControllerAnchors[channelKey];
  }
  const incomingPresetAnchorFields = Object.fromEntries(
    PRESET_ANCHOR_CONFIGS.flatMap(({ noteKey, channelKey }) => [
      [noteKey, Number.isFinite(preset[noteKey]) ? preset[noteKey] : undefined],
      ...(channelKey
        ? [[channelKey, Number.isFinite(preset[channelKey]) ? preset[channelKey] : undefined]]
        : []),
    ]),
  );

  const keyColorFlags = deriveKeyColorFlags(preset);

  return {
    ...settings,
    heji_anchor_ratio: "",
    heji_anchor_label: "",
    heji_anchor_root_label: "",
    key_colors_mode: undefined,
    auto_colors: undefined,
    spectrum_colors: undefined,
    ...clearedPresetAnchorFields,
    ...presetWithoutControllerAnchors,
    ...keyColorFlags,
    midiin_anchor_note: presetAnchorNote,
    midiin_anchor_channel: presetAnchorChannel,
    ...incomingPresetAnchorFields,
    controller_virtual_anchor_x: null,
    controller_virtual_anchor_y: null,
  };
};

export function mergeRestoredPresetIntoSettings(settings, preset) {
  const merged = mergePresetIntoSettings(settings, preset);
  for (const key of IO_SETTING_KEYS) {
    if (key in settings) merged[key] = settings[key];
    else delete merged[key];
  }
  return merged;
}

const snapshotOf = (s, modulationLibrary = []) => {
  return {
    tuningRecord: JSON.stringify(
      settingsToTuningRecord(s, {
        modulation_library: modulationLibrary,
      }),
    ),
    modulationLibrary: JSON.stringify(
      normalizeModulationHistory(modulationLibrary, { zeroCounts: true }),
    ),
  };
};

const isDirty = (snap, s, modulationLibrary = []) => {
  if (!snap) return false;
  if (
    JSON.stringify(
      settingsToTuningRecord(s, {
        modulation_library: modulationLibrary,
      }),
    ) !== snap.tuningRecord
  )
    return true;
  return (
    JSON.stringify(normalizeModulationHistory(modulationLibrary, { zeroCounts: true })) !==
    snap.modulationLibrary
  );
};

// localStorage key for the "restore on reload" preference
const PERSIST_ON_RELOAD_KEY = "hexatone_persist_on_reload";
const RESTORED_PRESET_PAYLOAD_KEY = "hexatone_restored_preset_payload";

function schedulePresetRuntimeReset(callback) {
  if (typeof callback !== "function") return;
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => callback());
    return;
  }
  setTimeout(() => callback(), 0);
}

const RESTORABLE_INPUT_RUNTIME_KEYS = [
  "midiin_controller_override",
  "midiin_mapping_target",
  "midi_passthrough",
  "midiin_mpe_input",
  "midiin_bend_flip",
  "midiin_bend_range",
  "midiin_scale_bend_range",
  "midiin_steps_per_channel",
  "midiin_channel_legacy",
  "midiin_pitchbend_mode",
  "midiin_pressure_mode",
  "wheel_to_recent",
  "wheel_scale_aware",
  "midi_wheel_semitones",
  "linnstrument_pitch_bend_mode",
  "linnstrument_pitch_bend_shape",
  "linnstrument_x_spike_reduction",
  "linnstrument_x_input_smoothing",
  "linnstrument_channel_allocation",
  "hakenaudio_x_glide_shaping",
  "hakenaudio_x_glide_mode",
  "hakenaudio_glide_flip_cc",
  "hakenaudio_pressure_velocity",
  "hakenaudio_note_off_delay",
  "hakenaudio_raster_throttle_ms",
  "hakenaudio_raster_stability",
  "hakenaudio_raster_filter_mode",
  "hakenaudio_raster_filter",
  "hakenaudio_apply_raster_in_pitch_bending",
  "hakenaudio_shape_x_glide_to_raster",
];

function buildRestorablePresetPayload(source, appliedSettings, savedLibrary = []) {
  if (!source || !appliedSettings) return null;
  const canonicalPreset =
    settingsToTuningRecord(appliedSettings, {
      modulation_library: savedLibrary,
    }) ??
    normalizeTuningRecord(
      {
        ...appliedSettings,
        modulation_library: normalizeModulationHistory(savedLibrary, { zeroCounts: true }),
      },
      { allowEmptyScale: true },
    );
  if (!canonicalPreset) return null;
  for (const key of RESTORABLE_INPUT_RUNTIME_KEYS) {
    if (appliedSettings[key] !== undefined) {
      canonicalPreset[key] = appliedSettings[key];
    }
  }
  return {
    source,
    name: canonicalPreset.name ?? null,
    preset: canonicalPreset,
  };
}

function writeRestorablePresetPayload(source, appliedSettings, savedLibrary = []) {
  const payload = buildRestorablePresetPayload(source, appliedSettings, savedLibrary);
  if (!payload) {
    sessionStorage.removeItem(RESTORED_PRESET_PAYLOAD_KEY);
    return;
  }
  sessionStorage.setItem(RESTORED_PRESET_PAYLOAD_KEY, JSON.stringify(payload));
}

function readRestorablePresetPayload() {
  const raw = sessionStorage.getItem(RESTORED_PRESET_PAYLOAD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    sessionStorage.removeItem(RESTORED_PRESET_PAYLOAD_KEY);
    return null;
  }
}

function clearRestorablePresetPayload() {
  sessionStorage.removeItem(RESTORED_PRESET_PAYLOAD_KEY);
}

function normalizePresetControllerInputSettings(preset = {}) {
  const normalized = { ...preset };
  const activePresetAnchorConfig = getPresetAnchorConfig(normalized);
  const controllerId =
    normalized.midiin_controller_override && normalized.midiin_controller_override !== "auto"
      ? normalized.midiin_controller_override
      : (activePresetAnchorConfig?.controllerId ?? null);
  const controller = controllerId ? getControllerById(controllerId) : null;
  const policy = getControllerMpeInputPolicy(controller);

  if (policy === "always") {
    normalized.midiin_mpe_input = true;
  } else if (policy === "never") {
    normalized.midiin_mpe_input = false;
  }

  return normalized;
}

function adjustPresetForRestore(preset = {}) {
  return normalizePresetControllerInputSettings({
    ...preset,
    hexSize: scaleHexSizeForScreen(preset.hexSize),
  });
}

/**
 * Manages preset state: active preset identity, dirty detection, and all
 * load/revert operations. Persists the active preset selection to sessionStorage
 * so it survives page refresh (when persistOnReload is enabled).
 *
 * @param {object}   settings         - Current app settings (initial value read on mount)
 * @param {function} setSettings      - Settings updater from useQuery
 * @param {object}   options
 * @param {object}   options.synthRef - Ref to the live synth (for prepare() on preset load)
 * @param {function} options.onUserInteraction - Called to mark the user as having interacted
 * @param {function} options.bumpImportCount - Bumps the scale reset token so TuneCell preview UI clears
 * @param {function} options.bumpPresetRuntimeReset - Forces a full musical-surface rebuild/reset
 *                                                    for sidebar preset refresh actions
 *                                               (required to start AudioContext)
 * @returns {{ activeSource, activePresetName, isPresetDirty,
 *             persistOnReload, setPersistOnReload,
 *             presetChanged, onLoadCustomPreset, onClearUserPresets,
 *             onRevertBuiltin, onRevertUser }}
 */
const usePresets = (
  settings,
  setSettings,
  {
    synthRef,
    onUserInteraction,
    bumpImportCount,
    bumpPresetRuntimeReset,
    currentModulationLibrary,
    setPresetModulationLibrary,
    onPresetModulationLibraryLoaded,
  },
) => {
  const [activeSource, setActiveSource] = useState(null);
  const [activePresetName, setActivePresetName] = useState(null);
  const [restoredOnMount, setRestoredOnMount] = useState(false);
  const [pendingRestoredPreset, setPendingRestoredPreset] = useState(null);
  // Snapshot stored in state so updating it triggers a re-render and
  // isPresetDirty recalculates correctly.
  const [savedPresetSnapshot, setSavedPresetSnapshot] = useState(null);
  // Defaults to false (clean start on reload) — opt-in, stored in localStorage.
  const [persistOnReload, setPersistOnReloadState] = useState(
    () => localStorage.getItem(PERSIST_ON_RELOAD_KEY) === "true",
  );

  const setPersistOnReload = (value) => {
    localStorage.setItem(PERSIST_ON_RELOAD_KEY, String(value));
    setPersistOnReloadState(value);
  };

  // On mount: restore the previously active preset from sessionStorage,
  // unless the user has opted into a clean start on every reload.
  useEffect(() => {
    if (!persistOnReload) return;

    const savedSource = sessionStorage.getItem("hexatone_preset_source");
    const savedName = sessionStorage.getItem("hexatone_preset_name");
    const savedPayload = readRestorablePresetPayload();

    if (!savedSource) {
      const currentScale = settings?.scale;
      const currentName = String(settings?.name ?? "").trim();
      if (Array.isArray(currentScale) && currentScale.length && !isBuiltinPresetName(currentName)) {
        setActiveSource("user");
        setActivePresetName(null);
        setSavedPresetSnapshot(null);
      }
      return;
    }
    if (savedSource === "user" && !savedName) {
      setActiveSource("user");
      setActivePresetName(null);
      setSavedPresetSnapshot(null);
      return;
    }
    if (!savedName) return;

    if (isIOS) {
      const payloadPreset =
        savedPayload?.source === savedSource && savedPayload?.name === savedName
          ? savedPayload.preset
          : null;
      setActiveSource(savedSource);
      setActivePresetName(savedName);
      if (payloadPreset) {
        const savedLibrary = normalizeModulationHistory(payloadPreset.modulation_library, {
          zeroCounts: true,
        });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setPendingRestoredPreset({ source: savedSource, name: savedName, preset: payloadPreset });
        return;
      }
      if (savedSource === "builtin") {
        const presetData = findPreset(savedName);
        if (presetData) {
          const savedLibrary = normalizeModulationHistory(presetData.modulation_library, {
            zeroCounts: true,
          });
          setPresetModulationLibrary(savedLibrary);
          onPresetModulationLibraryLoaded?.(savedLibrary);
        }
      } else if (savedSource === "user") {
        const preset = loadUserTunings().find((p) => p.name === savedName);
        if (preset) {
          const savedLibrary = normalizeModulationHistory(preset.modulation_library, {
            zeroCounts: true,
          });
          setPresetModulationLibrary(savedLibrary);
          onPresetModulationLibraryLoaded?.(savedLibrary);
        } else if (hasRestorableWorkspace(settings)) {
          setActiveSource("user");
          setActivePresetName(null);
          setSavedPresetSnapshot(null);
          return;
        }
      }
      setPendingRestoredPreset({ source: savedSource, name: savedName, preset: null });
      return;
    }

    if (
      savedPayload?.source === savedSource &&
      savedPayload?.name === savedName &&
      savedPayload?.preset
    ) {
      setRestoredOnMount(true);
      setActiveSource(savedSource);
      setActivePresetName(savedName);
      const adjustedPreset = adjustPresetForRestore(savedPayload.preset);
      const merged = mergeRestoredPresetIntoSettings(settings, adjustedPreset);
      const savedLibrary = normalizeModulationHistory(savedPayload.preset.modulation_library, {
        zeroCounts: true,
      });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      bumpImportCount?.();
      setSettings(() => merged, { updateUrl: false });
      schedulePresetRuntimeReset(bumpPresetRuntimeReset);
    } else if (savedSource === "builtin") {
      setRestoredOnMount(true);
      setActiveSource("builtin");
      setActivePresetName(savedName);
      const presetData = findPreset(savedName);
      if (presetData) {
        const adjustedPreset = adjustPresetForRestore(presetData);
        const merged = mergeRestoredPresetIntoSettings(settings, adjustedPreset);
        const savedLibrary = normalizeModulationHistory(presetData.modulation_library, {
          zeroCounts: true,
        });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
        bumpImportCount?.();
        setSettings(() => merged, { updateUrl: false });
        schedulePresetRuntimeReset(bumpPresetRuntimeReset);
      }
    } else if (savedSource === "user") {
      setRestoredOnMount(true);
      const customPresets = loadUserTunings();
      const preset = customPresets.find((p) => p.name === savedName);
      if (preset) {
        setActiveSource("user");
        setActivePresetName(preset.name);
        const adjustedPreset = adjustPresetForRestore(preset);
        const merged = mergeRestoredPresetIntoSettings(settings, adjustedPreset);
        const savedLibrary = normalizeModulationHistory(preset.modulation_library, {
          zeroCounts: true,
        });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
        bumpImportCount?.();
        setSettings(() => merged, { updateUrl: false });
        schedulePresetRuntimeReset(bumpPresetRuntimeReset);
      } else if (hasRestorableWorkspace(settings)) {
        setActiveSource("user");
        setActivePresetName(null);
        setSavedPresetSnapshot(null);
        setRestoredOnMount(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount-only: restores session preset on initial load; re-running on settings change would override user edits

  const activatePendingPreset = async () => {
    if (!pendingRestoredPreset) return false;
    const { source, name, preset: pendingPreset } = pendingRestoredPreset;
    if (isIOS) {
      await primeSharedSampleAudio();
    }
    await onUserInteraction?.();
    if (pendingPreset) {
      const adjustedPreset = adjustPresetForRestore(pendingPreset);
      const merged = mergeRestoredPresetIntoSettings(settings, adjustedPreset);
      bumpImportCount?.();
      const savedLibrary = normalizeModulationHistory(pendingPreset.modulation_library, {
        zeroCounts: true,
      });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      setSettings(() => merged);
    } else if (source === "builtin") {
      const presetData = findPreset(name);
      if (!presetData) return false;
      const adjustedPreset = adjustPresetForRestore(presetData);
      const merged = mergeRestoredPresetIntoSettings(settings, adjustedPreset);
      bumpImportCount?.();
      const savedLibrary = normalizeModulationHistory(presetData.modulation_library, {
        zeroCounts: true,
      });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      setSettings(() => merged);
    } else if (source === "user") {
      const preset = loadUserTunings().find((p) => p.name === name);
      if (!preset) return false;
      const adjustedPreset = adjustPresetForRestore(preset);
      const merged = mergeRestoredPresetIntoSettings(settings, adjustedPreset);
      bumpImportCount?.();
      const savedLibrary = normalizeModulationHistory(preset.modulation_library, {
        zeroCounts: true,
      });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      setSettings(() => merged);
    } else {
      return false;
    }
    setPendingRestoredPreset(null);
    setRestoredOnMount(false);
    return true;
  };

  const applyBuiltinPreset = (presetData) => {
    if (!presetData) return;
    const presetName = presetData.name;
    setRestoredOnMount(false);
    setPendingRestoredPreset(null);
    setActiveSource("builtin");
    setActivePresetName(presetName);
    sessionStorage.setItem("hexatone_preset_source", "builtin");
    sessionStorage.setItem("hexatone_preset_name", presetName);
    const adjustedPreset = adjustPresetForRestore(presetData);
    const merged = mergePresetIntoSettings(settings, adjustedPreset);
    bumpImportCount?.();
    const savedLibrary = normalizeModulationHistory(presetData.modulation_library, {
      zeroCounts: true,
    });
    setPresetModulationLibrary(savedLibrary);
    onPresetModulationLibraryLoaded?.(savedLibrary);
    setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
    writeRestorablePresetPayload("builtin", merged, savedLibrary);
    setSettings(() => merged);
    schedulePresetRuntimeReset(bumpPresetRuntimeReset);
    synthRef.current?.prepare?.();
  };

  const presetChanged = async (e) => {
    const presetName = e.target.value;
    if (!presetName) return;
    // Mark user interaction immediately so sample/audio warmup can begin in the
    // current gesture turn, but do not await prepare() here — iOS select
    // controls can lose the first committed value if the handler blocks before
    // the controlled value/state update lands.
    onUserInteraction();
    const presetData = findPreset(presetName);
    applyBuiltinPreset(presetData);
  };

  const onLoadBuiltinPreset = (preset) => {
    onUserInteraction();
    applyBuiltinPreset(preset);
  };

  const onLoadCustomPreset = (preset) => {
    onUserInteraction();
    setRestoredOnMount(false);
    setPendingRestoredPreset(null);
    setActiveSource("user");
    setActivePresetName(preset.name || null);
    sessionStorage.setItem("hexatone_preset_source", "user");
    if (preset.name) {
      sessionStorage.setItem("hexatone_preset_name", preset.name);
    } else {
      sessionStorage.removeItem("hexatone_preset_name");
    }
    const adjustedPreset = adjustPresetForRestore(preset);
    const merged = mergePresetIntoSettings(settings, adjustedPreset);
    bumpImportCount?.();
    const savedLibrary = normalizeModulationHistory(preset.modulation_library, {
      zeroCounts: true,
    });
    setPresetModulationLibrary(savedLibrary);
    onPresetModulationLibraryLoaded?.(savedLibrary);
    setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
    writeRestorablePresetPayload("user", merged, savedLibrary);
    setSettings(() => merged);
    schedulePresetRuntimeReset(bumpPresetRuntimeReset);
  };

  const onClearUserPresets = () => {
    const remaining = loadUserTunings();
    setActiveSource(null);
    setActivePresetName(null);
    setPendingRestoredPreset(null);
    setPresetModulationLibrary([]);
    onPresetModulationLibraryLoaded?.([]);
    sessionStorage.removeItem("hexatone_preset_source");
    sessionStorage.removeItem("hexatone_preset_name");
    clearRestorablePresetPayload();

    if (remaining.length > 0) {
      // Load the first remaining preset after the deleted one(s)
      const preset = remaining[0];
      setActiveSource("user");
      setActivePresetName(preset.name);
      sessionStorage.setItem("hexatone_preset_source", "user");
      sessionStorage.setItem("hexatone_preset_name", preset.name);
      const merged = mergePresetIntoSettings(settings, preset);
      const savedLibrary = normalizeModulationHistory(preset.modulation_library, {
        zeroCounts: true,
      });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      writeRestorablePresetPayload("user", merged, savedLibrary);
      setSettings(() => merged);
    } else {
      // No user presets remain — return to the blank Hexatone state while
      // preserving runtime/device settings from the current session.
      clearScaleSettings();
      clearRestorablePresetPayload();
      const merged = mergePresetIntoSettings(settings, buildBlankPresetSettings());
      setSavedPresetSnapshot(null);
      bumpImportCount?.();
      setSettings(() => merged);
      schedulePresetRuntimeReset(bumpPresetRuntimeReset);
      synthRef.current?.prepare?.();
    }
  };

  const onRevertBuiltin = () => {
    onUserInteraction();
    setRestoredOnMount(false);
    setPendingRestoredPreset(null);
    if (activePresetName) {
      const presetData = findPreset(activePresetName);
      const adjustedPreset = {
        ...adjustPresetForRestore(presetData),
      };
      const merged = mergePresetIntoSettings(settings, adjustedPreset);
      bumpImportCount?.();
      const savedLibrary = normalizeModulationHistory(presetData.modulation_library, {
        zeroCounts: true,
      });
      setPresetModulationLibrary(savedLibrary);
      onPresetModulationLibraryLoaded?.(savedLibrary);
      setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
      writeRestorablePresetPayload("builtin", merged, savedLibrary);
      setSettings(() => merged);
      schedulePresetRuntimeReset(bumpPresetRuntimeReset);
    }
  };

  const onRevertUser = () => {
    onUserInteraction();
    setRestoredOnMount(false);
    setPendingRestoredPreset(null);
    if (activePresetName) {
      const saved = loadUserTunings().find((p) => p.name === activePresetName);
      if (saved) {
        const adjustedPreset = adjustPresetForRestore(saved);
        const merged = mergePresetIntoSettings(settings, adjustedPreset);
        bumpImportCount?.();
        const savedLibrary = normalizeModulationHistory(saved.modulation_library, {
          zeroCounts: true,
        });
        setPresetModulationLibrary(savedLibrary);
        onPresetModulationLibraryLoaded?.(savedLibrary);
        setSavedPresetSnapshot(snapshotOf(merged, savedLibrary));
        writeRestorablePresetPayload("user", merged, savedLibrary);
        setSettings(() => merged);
        schedulePresetRuntimeReset(bumpPresetRuntimeReset);
      }
    }
  };

  // Called by useSettingsChange when scale_divide fires — the user has
  // generated a new scale that is no longer tied to any loaded preset.
  const onUserScaleEdit = (name) => {
    setActiveSource("user");
    setRestoredOnMount(false);
    setPendingRestoredPreset(null);
    setActivePresetName(name || null);
    sessionStorage.setItem("hexatone_preset_source", "user");
    if (name) {
      sessionStorage.setItem("hexatone_preset_name", name);
    } else {
      sessionStorage.removeItem("hexatone_preset_name");
    }
    setSavedPresetSnapshot(null);
  };

  const isPresetDirty = useMemo(
    () => isDirty(savedPresetSnapshot, settings, currentModulationLibrary),
    [savedPresetSnapshot, settings, currentModulationLibrary],
  );

  return {
    activeSource,
    activePresetName,
    restoredOnMount,
    pendingRestoredPreset,
    isPresetDirty,
    persistOnReload,
    setPersistOnReload,
    activatePendingPreset,
    presetChanged,
    onLoadBuiltinPreset,
    onLoadCustomPreset,
    onClearUserPresets,
    onRevertBuiltin,
    onRevertUser,
    onUserScaleEdit,
  };
};

export default usePresets;
